import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AppConfig } from 'src/app/config/app-config';
import { AppConfigService } from 'src/app/config/app-config.service';
import { TekstResourcePipe } from 'src/app/core/pipes/tekstresource.pipe';
import { Focusable } from 'src/app/core/services/autofocus/focusable';
import { ProfilAdresse } from '../../models/profil-adresse.model';

declare const adressevaelger: {
  adressevaelger: (
    inputElement: HTMLElement,
    options: {
      select: (selected: DawaAddress) => void;
      maksimum: number;
      medtagForeloebige: boolean;
      token: string;
    }
  ) => unknown;
};

@Component({
  selector: 'bom-profil-address-input',
  templateUrl: './profil-address-input.component.html',
  styleUrls: ['./profil-address-input.component.scss'],
})
export class ProfilAddressInputComponent implements OnInit, Focusable {
  @Input()
  addressString: string;

  @Output()
  addressSelected = new EventEmitter<ProfilAdresse>();

  @ViewChild('input', { static: true })
  input: ElementRef;

  @Input()
  form: FormGroup;

  private appConfig: AppConfig;

  constructor(private tekstResource: TekstResourcePipe, appConfigService: AppConfigService) {
    this.appConfig = appConfigService.getConfig();
  }

  ngOnInit() {
    const input = this.input.nativeElement as HTMLInputElement;
    adressevaelger.adressevaelger(input, {
      select: (dawaAddress: DawaAddress) => {
        this.addressSelected.emit(this.mapToPostAdresse(dawaAddress));
      },
      maksimum: 20,
      medtagForeloebige: true,
      token: this.appConfig.adressevaelgerToken,
    });

    // Drill-down suggestions set input.value directly (no `select`, no 'input' event),
    // so commit it ourselves on the bubbled click/keyup.
    const commitPickedSuggestion = (event: Event) => {
      if (!(event.target as HTMLElement).closest('.adressevaelger-suggestion')) {
        return;
      }
      const value = input.value;
      if (value && value !== this.addressString) {
        this.addressSelected.emit(this.mapToAdresse(value));
      }
    };
    input.parentElement?.addEventListener('click', commitPickedSuggestion);
    input.parentElement?.addEventListener('keyup', commitPickedSuggestion);
  }

  onBlur() {
    // Commit a manually typed address. Delayed so a suggestion click (whose
    // blur fires first) is committed by commitPickedSuggestion instead.
    setTimeout(() => {
      const tekstfeltAdresse = this.input.nativeElement.value;
      if (tekstfeltAdresse !== this.addressString && tekstfeltAdresse) {
        this.addressSelected.emit(this.mapToAdresse(tekstfeltAdresse));
      }
    });
  }

  private mapToAdresse(tekstfeltAdresse: string): ProfilAdresse {
    tekstfeltAdresse = tekstfeltAdresse.trim();
    const profilAdresse: ProfilAdresse = {
      indtastetAdresse: tekstfeltAdresse,
    };
    this.addressString = tekstfeltAdresse;
    this.form.controls['adresse'].setValue(tekstfeltAdresse);
    this.form.controls['adresse'].markAsPristine();
    return profilAdresse;
  }

  private mapToPostAdresse(dawaAdr: DawaAddress): ProfilAdresse {
    const profilAdresse: ProfilAdresse = {
      indtastetAdresse: dawaAdr.adresse.adressebetegnelse,
      postAdresse: {
        vejnavn: dawaAdr.adresse.husnummer?.vejnavn,
        husnummer: dawaAdr.adresse.husnummer.husnummertekst,
        doerbetegnelse: dawaAdr.adresse.doerbetegnelse,
        etage: dawaAdr.adresse.etagebetegnelse,
        postnummer: dawaAdr.adresse.husnummer.postnummer.postnr,
        postdistrikt: dawaAdr.adresse.husnummer.postnummer.navn,
      },
    };
    this.addressString = dawaAdr.adresse.adressebetegnelse;
    this.form.controls['adresse'].setValue(dawaAdr.adresse.adressebetegnelse);
    this.form.controls['adresse'].markAsPristine();
    return profilAdresse;
  }

  setFocus() {
    this.input.nativeElement.focus({ preventScroll: true });
  }
}

export interface DawaAddress {
  caretpos: number;
  adresse: DawaAddressData;
  forslagstekst: string;
  stormodtagerpostnr: boolean;
  tekst: string;
  type: string;
}

export interface DawaAddressData {
  adressebetegnelse: string;
  doerbetegnelse?: string;
  etagebetegnelse?: string;
  husnummer: Husnummer;
}

export interface Husnummer {
  vejnavn: string;
  husnummertekst: string;
  postnummer: Postnummer;
}

export interface Postnummer {
  postnr: string;
  navn: string;
}
